/**
 * Category Store - Zustand State Management
 * 
 * Manages category state with hierarchical tree support.
 * 
 * @validates Requirements 5.1-5.6 - Category management
 */

import { create } from 'zustand';
import {
  Category,
  CategoryTreeNode,
  CategoryBaseType,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '@budgira/shared';
import { categoryService } from '../services/category.service';
import i18n from '../i18n';

interface CategoryState {
  categories: CategoryTreeNode[];
  flatCategories: Category[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCategories: () => Promise<void>;
  createCategory: (data: CreateCategoryRequest) => Promise<Category | null>;
  updateCategory: (id: string, data: UpdateCategoryRequest) => Promise<Category | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  updateCategoryLogo: (id: string, logoUrl: string) => Promise<Category | null>;
  addAllTemplates: () => Promise<number>;
  resetToTemplates: () => Promise<{ deleted: number; added: number } | null>;
  removeTemplate: () => Promise<number>;
  purgeUnused: () => Promise<number>;
  getCategoriesByType: (baseType: CategoryBaseType) => CategoryTreeNode[];
  findCategoryById: (id: string) => Category | undefined;
  clearError: () => void;
}

/**
 * Flatten tree structure to a flat list for easier lookup
 */
function flattenTree(nodes: CategoryTreeNode[]): Category[] {
  const result: Category[] = [];
  
  function traverse(node: CategoryTreeNode) {
    result.push(node);
    if (node.children) {
      node.children.forEach(traverse);
    }
  }
  
  nodes.forEach(traverse);
  return result;
}

/**
 * Map backend error codes to localized messages
 */
function localizeCategoryError(err: unknown): string {
  // Extract error code from API response
  let errorCode: string | undefined;
  let errorMessage: string | undefined;
  
  if (err && typeof err === 'object') {
    // Axios error structure
    const axiosError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }
  
  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    'HAS_ASSOCIATED_OPERATIONS': 'categories.errors.hasAssociatedOperations',
    'HAS_CHILD_CATEGORIES': 'categories.errors.hasChildCategories',
    'CANNOT_MODIFY_SYSTEM_CATEGORY': 'categories.errors.cannotModifySystem',
    'CATEGORY_NOT_FOUND': 'categories.errors.notFound',
    'HIERARCHY_LIMIT_EXCEEDED': 'categories.errors.hierarchyLimit',
    'BASE_TYPE_REQUIRED': 'categories.errors.baseTypeRequired',
  };
  
  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }
  
  // Fallback to backend message or generic error
  return errorMessage || String(i18n.t('categories.errors.generic'));
}

export const useCategoryStore = create<CategoryState>((set, get) => ({
  categories: [],
  flatCategories: [],
  isLoading: false,
  error: null,

  fetchCategories: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.getTree();
      if (response.success && response.data) {
        const categories = response.data;
        set({ 
          categories, 
          flatCategories: flattenTree(categories),
          isLoading: false 
        });
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
    }
  },

  createCategory: async (data: CreateCategoryRequest): Promise<Category | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.create(data);
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateCategory: async (id: string, data: UpdateCategoryRequest): Promise<Category | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.update(id, data);
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  deleteCategory: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.delete(id);
      if (response.success) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return true;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return false;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  updateCategoryLogo: async (id: string, logoUrl: string): Promise<Category | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.updateLogo(id, logoUrl);
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  addAllTemplates: async (): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.addAllTemplates();
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data.count;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return 0;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  resetToTemplates: async (): Promise<{ deleted: number; added: number } | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.resetToTemplates();
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return null;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  removeTemplate: async (): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.removeTemplate();
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data.count;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return 0;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  purgeUnused: async (): Promise<number> => {
    set({ isLoading: true, error: null });
    try {
      const response = await categoryService.purgeUnused();
      if (response.success && response.data) {
        // Refresh the tree to get updated structure
        await get().fetchCategories();
        return response.data.count;
      } else {
        set({ error: response.error?.message || String(i18n.t('categories.errors.generic')), isLoading: false });
        return 0;
      }
    } catch (err) {
      const message = localizeCategoryError(err);
      set({ error: message, isLoading: false });
      return 0;
    }
  },

  getCategoriesByType: (baseType: CategoryBaseType): CategoryTreeNode[] => {
    return get().categories.filter((c) => c.baseType === baseType);
  },

  findCategoryById: (id: string): Category | undefined => {
    return get().flatCategories.find((c) => c.id === id);
  },

  clearError: () => set({ error: null }),
}));

export default useCategoryStore;


